<script setup lang="ts">
import ConsoleElement from '../ConsoleElement.vue';
import { Order } from '@/api/order/types';
import { Device } from '@/device';
import { InstallProperties } from '@/interfaces/properties.interface';
import { Log, Logger } from '@/logger';
import { ProcedureExecutor } from '@/procedures/procedures';
import { StateMachineEvent, StateMachineSnapshot } from '@/stateMachine';
import { ref } from 'vue';

const props = defineProps<{
  snapshot: StateMachineSnapshot;
  send: (event: StateMachineEvent) => void;
  step: 'install' | 'reset' | 'verify';
}>();

const startText = ref('');
const finished = ref(false);
const finishedText = ref('');
const inProgress = ref(false);
const inProgressText = ref('');
const failed = ref(false);
const failedText = ref('');
const progress = ref(0);

switch (props.step) {
  case 'install':
    startText.value = "Démarrer l'installation sur";
    finishedText.value = 'Installation terminée !';
    inProgressText.value = "En cours d'installation...";
    failedText.value = "Erreur d'installation";
    break;
  case 'reset':
    startText.value = 'Démarrer le nettoyage sur';
    finishedText.value = 'Nettoyage terminé !';
    inProgressText.value = 'En cours de nettoyage...';
    failedText.value = 'Erreur de nettoyage';
    break;
  case 'verify':
    startText.value = 'Démarrer la validation sur';
    finishedText.value = 'Validation terminée !';
    inProgressText.value = 'En cours de validation...';
    failedText.value = 'Erreur de validation';
    break;
  default:
    break;
}

const device = props.snapshot.context.device as Device;
const order = props.snapshot.context.order as Order;
const properties = props.snapshot.context.properties as InstallProperties;

const logs = ref(new Array<Log>());
const logger = new Logger(logs.value);
const executor = new ProcedureExecutor(order.procedure.procedure, properties, device, logger);

executor.progressCb = (newProgress: number) => {
  progress.value = newProgress;
};

const executeProcedure = async () => {
  finished.value = false;
  failed.value = false;
  inProgress.value = true;

  if (props.step === 'reset') {
    failed.value = !(await executor.reset());
  } else if (props.step === 'verify') {
    failed.value = !(await executor.validate());
  } else {
    failed.value = !(await executor.install());
  }

  if (!failed.value) {
    finished.value = true;
    if (props.step === 'reset') {
      props.send({ type: 'headset.cleaned' });
    } else if (props.step === 'verify') {
      props.send({ type: 'headset.validated' });
    } else {
      props.send({ type: 'headset.installed' });
    }
  }
};
</script>

<template>
  <div class="d-flex flex-column h-100">
    <div v-if="!inProgress" class="flex-grow-1 text-center d-flex justify-content-center flex-column align-items-center m-3">
      <p>{{ startText }} {{ device.getDeviceName() }} ({{ device.getDeviceId() }})</p>
      <button class="btn btn-primary" type="button" @click="executeProcedure">Démarrer</button>
    </div>
    <div v-else-if="inProgress" class="flex-grow-1 text-center d-flex justify-content-center flex-column align-items-center m-3">
      <template v-if="finished">
        <p>{{ finishedText }}</p>
        <div class="progress">
          <div
            class="progress-bar bg-success"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
            :style="{ width: progress + '%' }"
          ></div>
        </div>
      </template>
      <template v-else-if="!failed">
        <p>{{ inProgressText }}</p>
        <div class="progress">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
            :style="{ width: progress + '%' }"
          ></div>
        </div>
      </template>
      <template v-else>
        <p>{{ failedText }}</p>
        <div class="progress">
          <div
            class="progress-bar bg-danger"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
            :style="{ width: progress + '%' }"
          ></div>
        </div>
        <button class="btn btn-warning mt-3" type="button" @click="executeProcedure">Réessayer</button>
      </template>
      <ConsoleElement :logs="logs" />
    </div>
  </div>
</template>

<style scoped>
.progress {
  width: 100%;
  height: 1em;
}
</style>
